import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useActiveCampaigns } from '../../hooks/useCampaigns';
import PromoBanner from './PromoBanner';
import './campaigns.css';

const AUTO_PLAY_MS = 6000;

const CampaignCarousel = () => {
  const { pathname } = useLocation();
  const isHomepage = pathname === '/';
  const { t } = useTranslation('common');
  const { data: campaigns = [], isLoading } = useActiveCampaigns({ enabled: isHomepage });
  const [current, setCurrent] = useState(0);
  const count = campaigns.length;

  useEffect(() => {
    setCurrent(0);
  }, [count]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % count);
    }, AUTO_PLAY_MS);
    return () => clearInterval(interval);
  }, [count]);

  const goTo = useCallback((index) => setCurrent(index), []);
  const next = useCallback(() => {
    if (count === 0) return;
    setCurrent((prev) => (prev + 1) % count);
  }, [count]);
  const prev = useCallback(() => {
    if (count === 0) return;
    setCurrent((prev) => (prev - 1 + count) % count);
  }, [count]);

  if (!isHomepage || isLoading || count === 0) return null;

  return (
    <section className="campaign-section" aria-label={t('promotions', { defaultValue: 'Promotions' })}>
      <div className="campaign-carousel">
        <div className="campaign-carousel__viewport">
          {campaigns.map((campaign, index) => (
            <div
              key={campaign.id}
              className={`campaign-carousel__slide ${index === current ? 'campaign-carousel__slide--active' : ''}`}
              aria-hidden={index !== current}
            >
              <PromoBanner campaign={campaign} />
            </div>
          ))}
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              className="campaign-carousel__nav campaign-carousel__nav--prev"
              onClick={prev}
              aria-label={t('previousSlide', { defaultValue: 'Previous slide' })}
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="campaign-carousel__nav campaign-carousel__nav--next"
              onClick={next}
              aria-label={t('nextSlide', { defaultValue: 'Next slide' })}
            >
              <ChevronRight size={22} />
            </button>
            <div className="campaign-carousel__dots" role="tablist" aria-label={t('slideNavigation', { defaultValue: 'Slides' })}>
              {campaigns.map((campaign, index) => (
                <button
                  key={campaign.id}
                  type="button"
                  role="tab"
                  aria-selected={index === current}
                  aria-label={`${t('slide', { defaultValue: 'Slide' })} ${index + 1}`}
                  className={`campaign-carousel__dot ${index === current ? 'campaign-carousel__dot--active' : ''}`}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CampaignCarousel;
